var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useEffect, useState, useSyncExternalStore } from "react";
import { useStableOkapiKy } from "../hooks/useStableOkapiKy";
import { fetchCurrentUser } from "../api/userService";
import { RoleEnum, RoleGroups } from "../types/RoleEnum";
var readActiveRolesFromStorage = function () {
    var stored = sessionStorage.getItem("activeRoles");
    if (!stored || stored === "undefined")
        return undefined;
    try {
        return JSON.parse(stored);
    }
    catch (_a) {
        return undefined;
    }
};
// Whether the last attempt to load the roles failed. Shared by every hook
// instance: the roles themselves are shared through sessionStorage.
var rolesLoadFailed = false;
var rolesLoadFailedListeners = new Set();
var setRolesLoadFailed = function (failed) {
    if (rolesLoadFailed === failed)
        return;
    rolesLoadFailed = failed;
    rolesLoadFailedListeners.forEach(function (listener) { return listener(); });
};
var subscribeRolesLoadFailed = function (listener) {
    rolesLoadFailedListeners.add(listener);
    return function () {
        rolesLoadFailedListeners.delete(listener);
    };
};
/**
 * True when the active roles could not be loaded — `useActiveRoles` then stays
 * undefined, so a caller waiting for the roles must give up instead.
 */
export var useActiveRolesLoadFailed = function () {
    return useSyncExternalStore(subscribeRolesLoadFailed, function () { return rolesLoadFailed; });
};
export var useActiveRoles = function () {
    var okapiKy = useStableOkapiKy();
    var _a = useState(function () {
        var stored = readActiveRolesFromStorage();
        // This instance is about to load the roles again: an earlier failure must
        // not open a gate before that attempt ends. Listeners learn the outcome.
        if (stored === undefined)
            rolesLoadFailed = false;
        return stored;
    }), activeRoles = _a[0], setActiveRoles = _a[1];
    useEffect(function () {
        var stored = sessionStorage.getItem("activeRoles");
        if (stored && stored !== "undefined") {
            setRolesLoadFailed(false);
            // Another hook instance already populated sessionStorage; sync state if
            // our lazy init missed it.
            setActiveRoles(function (prev) {
                var _a;
                if (prev !== undefined)
                    return prev;
                return (_a = readActiveRolesFromStorage()) !== null && _a !== void 0 ? _a : [];
            });
            return undefined;
        }
        var cancelled = false;
        (function () { return __awaiter(void 0, void 0, void 0, function () {
            var fetchedUser, roles, error_1;
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, fetchCurrentUser(okapiKy)];
                    case 1:
                        fetchedUser = _b.sent();
                        roles = (_a = fetchedUser === null || fetchedUser === void 0 ? void 0 : fetchedUser.activeRoles) !== null && _a !== void 0 ? _a : [];
                        sessionStorage.setItem("activeRoles", JSON.stringify(roles));
                        if (!cancelled)
                            setActiveRoles(roles);
                        return [3 /*break*/, 3];
                    case 2:
                        error_1 = _b.sent();
                        console.error("Error fetching user:", error_1);
                        setRolesLoadFailed(true);
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        }); })();
        return function () {
            cancelled = true;
        };
    }, [okapiKy]);
    return activeRoles;
};
var isSuperuser = function (activeRoles) {
    return activeRoles && activeRoles.includes(RoleEnum.SUPERUSER);
};
export var hasRole = function (activeRoles, requiredRole) {
    if (isSuperuser(activeRoles))
        return true;
    return activeRoles && activeRoles.includes(requiredRole);
};
export var hasAtLeastOneRole = function (activeRoles, requiredRole) {
    for (var _i = 0, requiredRole_1 = requiredRole; _i < requiredRole_1.length; _i++) {
        var role = requiredRole_1[_i];
        if (hasRole(activeRoles, role)) {
            return true;
        }
    }
    return false;
};
export var hasAtLeastOneRoleAndDontHaveOther = function (activeRoles, requiredRole, notRequiredRole) {
    return (hasAtLeastOneRole(activeRoles, requiredRole) &&
        !hasRole(activeRoles, notRequiredRole));
};
export var hasExactlyRoles = function (activeRoles, exactRoles) {
    var norm = function (r) {
        return String(r !== null && r !== void 0 ? r : "")
            .trim()
            .toUpperCase();
    };
    var act = (activeRoles !== null && activeRoles !== void 0 ? activeRoles : []).map(norm);
    var ex = (exactRoles !== null && exactRoles !== void 0 ? exactRoles : []).map(norm);
    if (act.length !== ex.length)
        return false;
    return ex.every(function (r) { return act.includes(r); });
};
export var isPublisher = function (activeRoles) {
    if (!activeRoles)
        return false;
    return RoleGroups.PUBLISHER_ROLES.some(function (role) { return hasRole(activeRoles, role); });
};
/**
 * Whether the collection publisher is locked to (and the collections list
 * pre-filtered by) the user's own publisher. Agency workers and collections
 * administrators work across all publishers, even with a publisher role.
 */
export var locksCollectionPublisherToOwn = function (activeRoles) {
    return isPublisher(activeRoles) &&
        !hasRole(activeRoles !== null && activeRoles !== void 0 ? activeRoles : [], RoleEnum.COLLECTIONS_ADMINISTRATOR) &&
        !hasRole(activeRoles !== null && activeRoles !== void 0 ? activeRoles : [], RoleEnum.AGENCY_WORKER);
};
export var isPublisherRoleOnly = function (activeRoles) {
    if (!activeRoles)
        return false;
    return (isPublisher(activeRoles) &&
        !hasRole(activeRoles, RoleEnum.AGENCY_WORKER) &&
        !hasRole(activeRoles, RoleEnum.SUPERUSER));
};
